import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";

export type StageDto = {
  id: string;
  phaseId: string;
  projectId: string;
  name: string;
  stageValue: string;
  description: string | null;
  status: string;
  sortOrder: number;
  startDate: string | null;
  endDate: string | null;
  completedAt: string | null;
};

export type PhaseDto = {
  id: string;
  projectId: string;
  name: string;
  phaseValue: string;
  description: string | null;
  status: string;
  sortOrder: number;
  startDate: string | null;
  endDate: string | null;
  completedAt: string | null;
  progress: number;
  counts: {
    tasks: number;
    meetings: number;
    actions: number;
    notes: number;
    ideas: number;
    decisions: number;
    milestones: number;
    budgets: number;
    expenses: number;
  };
  stages: StageDto[];
};

export type PhaseSelectorItem = {
  id: string;
  name: string;
  phaseValue: string;
  status: string;
  stages: Array<{
    id: string;
    name: string;
    stageValue: string;
    status: string;
  }>;
};

export type CreatePhasePayload = {
  name: string;
  phaseValue: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  sortOrder?: number;
  stages?: Array<{
    name: string;
    stageValue: string;
    description?: string | null;
    sortOrder?: number;
    startDate?: string | null;
    endDate?: string | null;
  }>;
};

export type UpdatePhasePayload = {
  name?: string;
  phaseValue?: string;
  description?: string | null;
  status?: string;
  startDate?: string | null;
  endDate?: string | null;
};

export type CreateStagePayload = {
  name: string;
  stageValue: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

export type UpdateStagePayload = {
  name?: string;
  stageValue?: string;
  description?: string | null;
  status?: string;
  startDate?: string | null;
  endDate?: string | null;
};

const KEY = ["project-phases"] as const;

export function usePhases(
  projectId: string,
): UseQueryResult<PhaseDto[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, projectId, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!projectId,
    queryFn: async () => {
      const res = await apiFetch<{ data: PhaseDto[] }>(
        `/projects/${projectId}/phases`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function usePhaseSelector(
  projectId: string | null | undefined,
): UseQueryResult<PhaseSelectorItem[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, projectId ?? "none", "selector", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!projectId,
    queryFn: async () => {
      const res = await apiFetch<{ data: PhaseSelectorItem[] }>(
        `/projects/${projectId}/phases/selector`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  qc.invalidateQueries({ queryKey: [...KEY, projectId] });
  qc.invalidateQueries({ queryKey: ["projects"] });
}

export function useCreatePhase(projectId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePhasePayload) =>
      apiFetch<{ data: PhaseDto }>(`/projects/${projectId}/phases`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useUpdatePhase(projectId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      phaseId,
      payload,
    }: {
      phaseId: string;
      payload: UpdatePhasePayload;
    }) =>
      apiFetch<{ data: PhaseDto }>(
        `/projects/${projectId}/phases/${phaseId}`,
        { method: "PUT", body: payload, token: accessToken },
      ),
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useUpdatePhaseStatus(projectId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, status }: { phaseId: string; status: string }) =>
      apiFetch<{ data: PhaseDto }>(
        `/projects/${projectId}/phases/${phaseId}/status`,
        { method: "PUT", body: { status }, token: accessToken },
      ),
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useDeletePhase(projectId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phaseId: string) =>
      apiFetch(`/projects/${projectId}/phases/${phaseId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useReorderPhases(projectId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: Array<{ id: string; sortOrder: number }>) =>
      apiFetch(`/projects/${projectId}/phases/reorder`, {
        method: "POST",
        body: { updates },
        token: accessToken,
      }),
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useCreateStage(projectId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      phaseId,
      payload,
    }: {
      phaseId: string;
      payload: CreateStagePayload;
    }) =>
      apiFetch(`/projects/${projectId}/phases/${phaseId}/stages`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useUpdateStage(projectId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      phaseId,
      stageId,
      payload,
    }: {
      phaseId: string;
      stageId: string;
      payload: UpdateStagePayload;
    }) =>
      apiFetch(
        `/projects/${projectId}/phases/${phaseId}/stages/${stageId}`,
        { method: "PUT", body: payload, token: accessToken },
      ),
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useUpdateStageStatus(projectId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      phaseId,
      stageId,
      status,
    }: {
      phaseId: string;
      stageId: string;
      status: string;
    }) =>
      apiFetch(
        `/projects/${projectId}/phases/${phaseId}/stages/${stageId}/status`,
        { method: "PUT", body: { status }, token: accessToken },
      ),
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useDeleteStage(projectId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, stageId }: { phaseId: string; stageId: string }) =>
      apiFetch(
        `/projects/${projectId}/phases/${phaseId}/stages/${stageId}`,
        { method: "DELETE", token: accessToken },
      ),
    onSuccess: () => invalidate(qc, projectId),
  });
}
